import React, { createContext, useCallback, useContext, useState } from 'react';

import type { AuthUser } from '@/types/auth';
import {
  clearSession,
  getStoredUser,
  startSession,
  updateStoredUser,
  updateTokens
} from '@/features/auth/context/tokenStorage';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** remember=true → localStorage (preživi zatvaranje preglednika), false → sessionStorage (nestane kad se tab/prozor zatvori). */
  login: (token: string, refreshToken: string, user: AuthUser, remember: boolean) => void;
  logout: () => void;
  /** Nakon uspješne promjene lozinke — svježi tokeni, mustChangePassword se gasi na postojećem korisniku. */
  completePasswordChange: (token: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);

  const login = useCallback(
    (token: string, refreshToken: string, newUser: AuthUser, remember: boolean) => {
      startSession(token, refreshToken, newUser, remember);
      setUser(newUser);
    },
    []
  );

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const completePasswordChange = useCallback((token: string, refreshToken: string) => {
    updateTokens(token, refreshToken);
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, mustChangePassword: false };
      updateStoredUser(updated);
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, logout, completePasswordChange }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>');
  return ctx;
};
